import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  useGetTagArticlesQuery,
  useGetTagBySlugQuery,
} from '@/services/newsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setArticleSlugs } from '@/store/news.slice';
import { ArticleFeedCard } from '@/components/news/ArticleFeedCard';
import { FeedSkeleton } from '@/components/news/FeedSkeleton';
import { SortToggle } from '@/components/news/SortToggle';
import { Loader } from '@/components/common/Loader';
import type { ArticleSummary } from '@/types/newsService';

/**
 * Tag-scoped article feed. Ported from
 * mobile-app/app/news-tag/[slug].tsx (`feat/news-revised`).
 */
export function NewsTag() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation('news');
  const dispatch = useAppDispatch();
  const locale = useAppSelector((s) => s.news.locale) || i18n.language || 'en';

  const [sort, setSort] = useState<'latest' | 'trending' | 'top'>('latest');
  const [page, setPage] = useState(1);
  const [articles, setArticles] = useState<ArticleSummary[]>([]);

  const { data: tagDetail, isLoading: isLoadingTag } = useGetTagBySlugQuery(slug, {
    skip: !slug,
  });

  const {
    data: result,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetTagArticlesQuery(
    { slug, page, limit: 15, locale, sort },
    { skip: !slug },
  );

  useEffect(() => {
    if (!result) return;
    setArticles((prev) => (page === 1 ? result.docs : [...prev, ...result.docs]));
  }, [result, page]);

  // Sync the slug list so ArticleDetail's swipe gesture can walk this feed.
  useEffect(() => {
    if (articles.length === 0) return;
    dispatch(setArticleSlugs(articles.map((a) => a.slug)));
  }, [articles, dispatch]);

  // Reset on sort change.
  useEffect(() => {
    setPage(1);
    setArticles([]);
  }, [sort]);

  // Infinite scroll sentinel.
  useEffect(() => {
    if (!result?.hasNextPage || isFetching) return;
    const sentinel = document.getElementById('news-tag-sentinel');
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setPage((p) => p + 1);
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [result?.hasNextPage, isFetching, articles.length]);

  const handleRetry = useCallback(() => void refetch(), [refetch]);

  if (isLoadingTag) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-6 pt-4">
      <header className="px-5">
        <h1 className="text-xl font-bold text-fg">{tagDetail?.name ?? slug}</h1>
        {tagDetail?.description ? (
          <p className="mb-2 mt-1 text-sm leading-5 text-fg-muted">
            {tagDetail.description}
          </p>
        ) : null}
        <div className="mb-2 flex gap-4">
          <span className="text-[13px] font-semibold text-brand">
            {t('tagPage.articleCount', {
              count: tagDetail?.articleCount ?? 0,
              defaultValue: '{{count}} articles',
            })}
          </span>
          <span className="text-[13px] font-semibold text-brand">
            {t('tagPage.followerCount', {
              count: tagDetail?.followerCount ?? 0,
              defaultValue: '{{count}} followers',
            })}
          </span>
        </div>
      </header>

      <SortToggle activeSort={sort} onSortChange={setSort} />

      {isLoading && articles.length === 0 ? (
        <FeedSkeleton />
      ) : isError && articles.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-8 py-16">
          <p className="text-center text-base text-fg-muted">
            {t('listPage.errorTitle', { defaultValue: "Couldn't load news" })}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-full bg-bg-secondary px-6 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80"
          >
            {t('listPage.errorRetry', { defaultValue: 'Retry' })}
          </button>
        </div>
      ) : articles.length === 0 ? (
        <div className="flex flex-col items-center px-8 py-16">
          <p className="text-center text-base text-fg-muted">
            {t('tagPage.empty', { defaultValue: 'No articles found' })}
          </p>
        </div>
      ) : (
        <div className="pt-2">
          {articles.map((article) => (
            <ArticleFeedCard key={article._id} article={article} locale={locale} />
          ))}
          <div id="news-tag-sentinel" className="h-6" />
          {isFetching && result?.hasNextPage && (
            <div className="flex justify-center py-4">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-brand" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
